import { TextField } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

type Props = {
  onChangeCount?: (newCount: number) => void;
  cardHeight: number;
  borderRadius: number;
  itemCount: number;
};

export const SystemCardInputField: React.FC<Props> = ({
  onChangeCount,
  cardHeight,
  borderRadius,
  itemCount,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const prevInputVal = useRef<number>(itemCount);
  const [count, setCount] = useState<string>(String(itemCount));

  useEffect(() => {
    setCount(String(itemCount));
  }, [itemCount]);

  useEffect(() => {
    // if the user clicks away from the inputref,
    // set the count to the current value
    // otherwise if the input is invalid, i.e. ""
    // set the current value to the previous value
    const handleClick = (e: MouseEvent) => {
      const input = inputRef.current;
      const prevVal = prevInputVal.current;

      if (!input || !prevVal) return;

      // if the click is outside of the input box
      if (!input.contains(e.target as Node)) {
        if (count === '') {
          // if current string is empty string, reset count state to what it was before
          setCount(String(prevVal));
          onChangeCount?.(prevVal);
        }
      }
    };

    window.addEventListener('click', handleClick);

    return () => window.removeEventListener('click', handleClick);
  }, [inputRef, prevInputVal, count, onChangeCount]);

  const updateCount = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;

    if (val === '') {
      prevInputVal.current = Number(count);
      setCount('');
      return;
    }

    const num = Number(val);

    if (isNaN(num) || !isFinite(num) || num <= 0) {
      return;
    }

    prevInputVal.current = Number(count);
    onChangeCount?.(num);
    setCount(val);
  };

  return (
    <TextField
      inputRef={inputRef}
      value={count}
      variant='outlined'
      onChange={updateCount}
      inputProps={{
        style: {
          padding: 0,
          height: '100%',
          textAlign: 'center',
        },
      }}
      sx={{
        height: `${cardHeight}px`,
        '& .MuiInputBase-root': {
          height: '100%',
          fontSize: 'inherit',
          backgroundColor: 'secondary.main',
          color: 'info.dark',
          fontWeight: 'bold',
          padding: 0,
          borderRadius: `${borderRadius}px`,
        },
        '& input': {
          height: '100%',
          padding: 0,
          boxSizing: 'border-box',
          textAlign: 'center',
        },
        '& fieldset': { top: 0 },
        '& fieldset legend': { display: 'none' },
      }}
    />
  );
};
